import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';

import { environment } from 'src/environments/environment';

import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { Dialog } from 'primeng/dialog';

import { DocListEntry } from '../../shared/doc-list-entry';
import { DocumentApiService } from '../../services/document-api.service';
import { DocTreeNode } from '../../shared/doc-tree-node';
import { buildPrimeNgTree } from '../../shared/doc-tree-builder';

/**
 * DocumentsTreeComponent
 *
 * Modal dialog that displays the full parent-child document hierarchy for a
 * given document. The tree is fetched in a single API call using
 * `GET /DocumentList/tree/:docId` backed by `dbo.udf_doctree_iTVF`.
 *
 * ### Inputs
 * | Name         | Type          | Description                                           |
 * |--------------|---------------|-------------------------------------------------------|
 * | `documentId` | `number`      | When set, loads the document by ID then rebuilds tree |
 * | `document`   | `DocListEntry`| Directly supply a document; tree reloads on change    |
 * | `display`    | `boolean`     | Controls dialog visibility (two-way via displayChange)|
 * | `header`     | `string`      | Dialog header title text                              |
 * | `disabled`   | `boolean`     | Disables PrimeNG tree interaction                     |
 *
 * ### Outputs
 * | Name               | Type                    | Description                          |
 * |--------------------|-------------------------|--------------------------------------|
 * | `selectedDocument` | `EventEmitter<DocListEntry>` | Emits when user confirms selection|
 * | `displayChange`    | `EventEmitter<boolean>` | Emits `false` when dialog is closed  |
 *
 * ### API calls (via DocumentApiService)
 * - `getDocTree(id)`     — fetches the complete tree in a single call (replaces N+1 pattern)
 * - `getDocListEntry(id)` — resolves a single document by ID (used with documentId input)
 *
 * The tree is built client-side using `buildPrimeNgTree()` from the flat,
 * path-ordered array returned by the API.
 */
@Component({
  selector: 'app-documents-tree',
  templateUrl: './documents-tree.component.html',
  styleUrls: ['./documents-tree.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentsTreeComponent implements OnInit, OnChanges, OnDestroy {

  treeQuery: Subscription;
  selectedDocQuery: Subscription;

  _outputDoc: DocListEntry;

  selectedNode: TreeNode;

  /** When set, loads the document by this ID then rebuilds the tree. */
  @Input() documentId: number;

  /** Directly supplied document; tree reloads when this reference changes. */
  @Input() document: DocListEntry;

  /** Emits the selected `DocListEntry` when the user confirms or clicks a node. */
  @Output() selectedDocument: EventEmitter<DocListEntry> = new EventEmitter();

  /** Dialog header text. */
  @Input()  header: string = "Дерево документов";

  modal: boolean = true;
  loading: boolean;

  /** Controls dialog visibility; supports two-way binding via `displayChange`. */
  @Input() display: boolean = false;

  /** When true, PrimeNG tree node interaction is disabled. */
  @Input() disabled: boolean = false;

  /** Emits `false` on dialog hide to support two-way `[(display)]` binding. */
  @Output() displayChange = new EventEmitter();

  docTreeNodes: TreeNode<DocListEntry>[] = [{}];

  @ViewChild('showdoctree') showDocumentsTree: Dialog;

  constructor(
    private docApi: DocumentApiService,
    private router: Router
  ) { }

  /** Unsubscribes `treeQuery` and `selectedDocQuery` subscriptions. */
  ngOnDestroy(): void {
    if (this.treeQuery) { this.treeQuery.unsubscribe(); }

    if (this.selectedDocQuery) { this.selectedDocQuery.unsubscribe(); }
  }

  /**
   * Loads a document by its numeric ID via the API, stores it in `document`,
   * then triggers a full tree reload.
   *
   * Called when the `documentId` input changes.
   *
   * @param docId The DOCID to load.
   */
  loadDocById( docId: number) {
    if (docId) {
      environment.production || console.log('*** DocumentsTreeComponent loadDocById: ===> document: ' + docId);

      this.selectedDocQuery =
        this.docApi.getDocListEntry(docId)
          .pipe(map(data => this.document = data[0]))
          .subscribe(() => {
            this.reloadDocTree();
          });
    }
  }

  /**
   * Fetches the complete document tree via a single API call to
   * `GET /DocumentList/tree/:docId`, then builds the PrimeNG tree structure
   * client-side using `buildPrimeNgTree()`.
   *
   * Replaces the legacy N+1 query pattern with a single SQL call backed by
   * `dbo.udf_doctree_iTVF`.
   *
   * `loading` is set to `true` before the request and cleared to `false` only
   * after the observable completes.
   */
  reloadDocTree(): void {
    if (!this.document?.DOCID) { return; }
    environment.production || console.log('*** DocumentsTreeComponent reloadDocTree: ===> document: ' + this.document.DOCID);

    this.loading = true;
    this.treeQuery = this.docApi.getDocTree(this.document.DOCID)
      .subscribe({
        next: (flatNodes: DocTreeNode[]) => {
          const [roots, selected] = buildPrimeNgTree(flatNodes, this.document.DOCID!);
          this.docTreeNodes = roots.length > 0 ? roots : [{}];
          if (selected) { this.selectedNode = selected; }
          this.loading = false;
        },
        error: (err) => {
          console.log('*** DocumentsTreeComponent reloadDocTree ERROR: ' + JSON.stringify(err));
          this.loading = false;
        }
      });
  }

  /** No-op initialisation — tree loading is driven by `ngOnChanges`. */
  ngOnInit(): void {
    console.log('*** DocumentsTreeComponent ngOnInit');
  }

  /**
   * Responds to input changes. Priority order:
   * 1. `documentId` — loads document by ID then rebuilds tree.
   * 2. `display` or `document` — rebuilds tree with the current document.
   *
   * Only one reload action fires per change-detection cycle to avoid duplicate
   * API calls when multiple inputs change simultaneously.
   */
  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      environment.production || console.log('*** DocumentsTreeComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(changes[propName].currentValue));
    }

    if (changes['documentId']) {
      this.loadDocById(changes['documentId'].currentValue);
    } else if (changes['display'] || changes['document']) {
      this.reloadDocTree();
    }
  }

  /**
   * Called by the PrimeNG tree `selectionChange` event (if wired via
   * `onChangeDocTree`). Stores the selected document and emits it immediately.
   *
   * @param e Event object carrying the newly selected `DocListEntry` as `value`.
   */
  onChangeDocTree(e: { value: DocListEntry }) {
    environment.production || console.log('*** DocumentsTreeComponent onChangeDocumentTree: ' + e.value);

    this._outputDoc = e.value;

    // console.log('*** DepartmentselectorComponent onChangeDepartment: Department: ' + this._departmentMC.CLIENTSNIKNAME);

    this.selectedDocument.emit(this._outputDoc);
  }

  /** Called by PrimeNG dialog `onShow` event. */
  onShowDialog() {
    environment.production || console.log('*** DocumentsTreeComponent onShow: ');
  }

  /** Called by PrimeNG dialog `onHide` event. Emits `false` to close the dialog via two-way binding. */
  onHideDialog() {
    environment.production || console.log('*** DocumentsTreeComponent onHide');
    this.displayChange.emit(false);
  }

  /**
   * Confirms the current selection. Emits `selectedDocument` with the currently
   * selected node and closes the dialog.
   *
   * @param _text Unused string literal passed from the template button.
   */
  onConfirm(_text: string) {
    environment.production || console.log('*** DocumentsTreeComponent onConfirm: ' + _text);
    this.selectedDocument.emit(this._outputDoc);
    this.display = false;
  }

  /**
   * Navigates to the edit page of the currently selected document and closes
   * the dialog. Does nothing if no document is selected.
   *
   * @param _text Unused string literal passed from the template button.
   */
  onGoToDocument(_text: string) {
    environment.production || console.log('*** DocumentsTreeComponent onGoToDocument: ' + _text);

    if (this._outputDoc) {
      this.router.navigate(['/edit-document', this._outputDoc.DOCTYPEID, this._outputDoc.DOCID]);

    }

    this.display = false;
  }

  /**
   * Handles PrimeNG tree `onNodeSelect` event.
   * Stores the selected node's document in `_outputDoc` for use by action buttons.
   *
   * @param event PrimeNG node select event containing the clicked `TreeNode`.
   */
  nodeSelect(event: { node: TreeNode<DocListEntry> }) {
    if (event.node) {
      environment.production || console.log('*** DocumentsTreeComponent onNodeSelect data:     ' + JSON.stringify(event.node.data));
      this._outputDoc = event.node.data;
    }
  }

  /**
   * Handles PrimeNG tree `onNodeExpand` event.
   * Currently used for debug logging only; children are pre-loaded at build time.
   *
   * @param event PrimeNG node expand event containing the expanded `TreeNode`.
   */
  nodeExpand(event: { node: TreeNode<DocListEntry> }) {
    if (event.node) {
      environment.production || console.log('*** DocumentsTreeComponent nodeExpand data:     ' + JSON.stringify(event.node.data));
    }
  }
}
